import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Dropdown } from '~/components/Dropdown';

import type { LabelData, LabelHierarchy } from '../../types';
import { mapLabelHierarchyToCheckboxTreeItem } from '../../utils/labels';
import { CheckboxTree } from '../CheckboxTree';
import type { CheckboxTreeItem } from '../CheckboxTree/types';
import { flattenCheckboxTree } from '../CheckboxTree/utils';
import { useOnClickOutside } from './useOutSideClick';

interface LabelFilterProps {
  originalLabels: LabelData[];
  labelHierarchyData: LabelHierarchy[] | undefined;
  isLoading: boolean;
  disabled?: boolean;
  onApply: () => void;
}

const PORTAL_CONTENT_CLASS = 'label-filter-dropdown-content';

export function LabelFilter({
  originalLabels,
  labelHierarchyData,
  isLoading,
  disabled,
  onApply,
}: LabelFilterProps) {
  const { t } = useTranslation('label-perf');
  const { watch, setValue } = useFormContext();
  const labelFlatItems = watch('labels');
  const [labelTreeItems, setLabelTreeItems] = useState<CheckboxTreeItem[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(dropdownRef, onApply, `.${PORTAL_CONTENT_CLASS}`);

  useEffect(() => {
    if (labelHierarchyData) {
      const originalLabelIds = new Set(
        originalLabels.map((label) => label.labelId),
      );

      setLabelTreeItems(
        mapLabelHierarchyToCheckboxTreeItem(
          labelHierarchyData,
          originalLabelIds,
        ),
      );
    }
  }, [labelHierarchyData, originalLabels]);

  useEffect(() => {
    if (isLoading || !labelTreeItems.length) {
      return;
    }

    const newLabelFlatItems = flattenCheckboxTree(labelTreeItems)
      .filter((label) => label.checked)
      .map((label) => ({
        labelId: parseInt(label.id, 10),
        name: label.label,
      }))
      .sort((a, b) => a.labelId - b.labelId);

    setValue('labels', newLabelFlatItems, {
      shouldDirty: true,
    });
  }, [labelTreeItems, setValue, isLoading]);

  const handleLabelsTreeChange = useCallback((newItems: CheckboxTreeItem[]) => {
    setLabelTreeItems(newItems);
  }, []);

  if (isLoading) {
    return (
      <div className="w-full" ref={dropdownRef}>
        <Dropdown
          options={[]}
          placeholder={t('body.loading')}
          contentClassName="z-10 w-1/2 overflow-hidden"
          disabled
        >
          <div data-testid="label-filter-loading-indicator">
            {t('body.loading')}
          </div>
        </Dropdown>
      </div>
    );
  }

  return (
    <div className="w-full" ref={dropdownRef}>
      <Dropdown
        options={[]}
        placeholder={
          labelFlatItems.length > 0
            ? t('body.labelsWithCount', {
                count: labelFlatItems.length,
              })
            : t('body.selectLabels')
        }
        // 3. Append the unique class to the existing classes
        contentClassName={`z-50 w-1/2 overflow-x-hidden ${PORTAL_CONTENT_CLASS}`}
        scrollableContentClassName="max-h-[34rem]"
        disabled={disabled}
      >
        <CheckboxTree
          value={labelTreeItems}
          setValue={setLabelTreeItems}
          onChange={handleLabelsTreeChange}
        />
      </Dropdown>
    </div>
  );
}

LabelFilter.displayName = 'LabelFilter';
